/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable prefer-destructuring */
import Joi from '@hapi/joi';
import React from 'react';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchPost } from '../../../store/fetch';
import {
    Accordion,
    DataPanelRow,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    Panel,
    PanelBody,
    PanelBodyText,
    PanelHorizontalSpacing,
    Tray,
} from '../../elements';
import { showSavedToast } from '../../utils/toastMessages';
import {
    ValidatingEditPanelSaveButton,
    ValidatingEditPanelSwitch,
    ValidatingForm,
    ValidatingFormContext,
    ValidatingFormInputText,
    ValidatingFormTextArea,
} from '../../validatingElements';
import { AssetDropzone } from '../Common/AssetDropzone';
import { AssetTemplateList } from '../Common/AssetTemplateList';
import { IElement } from '../Interfaces/IElement';
import { IManifestation } from '../Interfaces/IManifestation';
import { IUnit } from '../Interfaces/IUnit';
import { IUtProject } from '../Interfaces/IUtProject';
import { EditNominalValue } from './EditNominalUtProjects';
import { EditProbeMhz } from './EditProbeMhzUtProjects';
import { MergeProject } from './MergeSplitUtProjects';
import { ProjectMeasurements } from './ProjectMeasurements';

const schema = Joi.object().keys({
    name: Joi.string().required(),
    description: Joi.string().allow(null, ''),
    scanLineLabel: Joi.string().allow(null, ''),
    positionLabel: Joi.string().allow(null, ''),
    isValidated: Joi.boolean(),
    isScanLineOnXAxis: Joi.boolean().allow(null, ''),
});

export function SurveyEditUtProjects(props: {
    objectId?: string | undefined;
    surveyId?: string | undefined;
    elementId?: string | undefined;
    element?: IElement | undefined;
    unit?: IUnit | undefined;
    manifestation?: IManifestation | undefined;
    onItemChanged(): void;
}) {
    const translate = useTranslate();

    const [utProjectReloadKey, setUtProjectReloadKey] = React.useState(0);

    async function removeUtProjects(utProjectId: string) {
        await fetchPost(`/api/utProjects/${utProjectId}`, undefined, {
            elementId: null,
            unitId: null,
            manifestationId: null,
            objectId: null,
            surveyId: null,
        });
        props.onItemChanged();
    }

    const onDropzoneComplete = async () => {
        props.onItemChanged();
    };

    const documentChanged = async () => {
        props.onItemChanged();
    };

    let utProjects: IUtProject[] = [];
    if (props.element && props.element.utProjects.length > 0) {
        utProjects = props.element.utProjects;
    }
    if (props.unit && props.unit.utProjects.length > 0) {
        utProjects = props.unit.utProjects;
    }
    if (props.manifestation && props.manifestation.utProjects.length > 0) {
        utProjects = props.manifestation.utProjects;
    }

    return (
        <Tray>
            {(utProjects.length > 0 &&
                utProjects.map((utProject) => (
                    <Panel key={utProject.id}>
                        <PanelBody>
                            <ProjectMeasurements project={utProject} isEditMode={true} key={utProjectReloadKey} />
                        </PanelBody>
                        <div className="panel-body bg-light dark">
                            <Accordion title={translate('common.settings')} index={utProject.id} key={utProject.id}>
                                <ValidatingForm
                                    schema={schema}
                                    itemUrl={`/api/utProjects/${utProject.id}`}
                                    id={utProject.id}
                                    onSubmitted={async () => {
                                        void showSavedToast(translate);
                                        props.onItemChanged();
                                    }}
                                >
                                    <ValidatingFormContext.Consumer>
                                        {(state) => (
                                            <>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={12}>
                                                        <ValidatingFormInputText
                                                            name="name"
                                                            topLabel={translate('common.name')}
                                                            placeholder={translate('common.name')}
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <ValidatingFormInputText name="scanLineLabel" topLabel={translate('project.scanLineLabel')} />
                                                    </EditPanelCell>
                                                    <EditPanelCell rowCount={6}>
                                                        <ValidatingFormInputText name="positionLabel" topLabel={translate('project.positionLabel')} />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <ValidatingEditPanelSwitch
                                                            name="isValidated"
                                                            id={`validated${utProject.id}`}
                                                            placeholder={translate('project.isValidated')}
                                                            onText="On"
                                                            offText="Off"
                                                        />
                                                    </EditPanelCell>
                                                    <EditPanelCell rowCount={6}>
                                                        <ValidatingEditPanelSwitch
                                                            name="isScanLineOnXAxis"
                                                            id={`xAxis${utProject.id}`}
                                                            placeholder={translate('project.scanLineXAxis')}
                                                            onText="On"
                                                            offText="Off"
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={12}>
                                                        <ValidatingFormTextArea
                                                            name="description"
                                                            topLabel={translate('common.description')}
                                                            placeholder={translate('common.description')}
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={12}>{utProject.nominalPositionWidthValues}</EditPanelCell>
                                                </EditPanelRow>

                                                <EditPanelButtons>
                                                    {utProjects.length > 1 && (
                                                        <MergeProject
                                                            projectId={utProject.id}
                                                            projectName={utProject.name}
                                                            projects={utProjects}
                                                            onMerged={async () => props.onItemChanged()}
                                                        />
                                                    )}
                                                    <EditNominalValue
                                                        projectId={utProject.id}
                                                        projectName={utProject.name}
                                                        onSaved={async () => props.onItemChanged()}
                                                    />
                                                    <EditProbeMhz
                                                        projectId={utProject.id}
                                                        projectName={utProject.name}
                                                        onSaved={async () => {
                                                            props.onItemChanged();
                                                            setUtProjectReloadKey((current) => current + 1);
                                                        }}
                                                    />
                                                    <EditPanelButton
                                                        primary={false}
                                                        small
                                                        text={translate('project.removeLink')}
                                                        onClick={async () => removeUtProjects(state.item.id)}
                                                    />
                                                    <ValidatingEditPanelSaveButton small />
                                                </EditPanelButtons>
                                            </>
                                        )}
                                    </ValidatingFormContext.Consumer>
                                </ValidatingForm>
                            </Accordion>

                            <Accordion title={translate('survey.documents')} index={`${utProject.id}doc`} key={`${utProject.id}doc`}>
                                <PanelHorizontalSpacing>
                                    <AssetDropzone
                                        acceptedFiles=".doc,.docx,.pdf,.xls,.xlsx,.png,.jpg"
                                        onComplete={onDropzoneComplete}
                                        objectId={props.objectId}
                                        surveyId={props.surveyId}
                                        utProjectId={utProject.id}
                                        autoProcessQueue={true}
                                        maxFileSizeMb={45_000}
                                    />
                                </PanelHorizontalSpacing>
                                <DataPanelRow>
                                    <PanelHorizontalSpacing>
                                        <AssetTemplateList
                                            assets={utProject.assets}
                                            canRemove={true}
                                            onAssetUpdated={documentChanged}
                                            surveyId={props.surveyId}
                                        />
                                    </PanelHorizontalSpacing>
                                </DataPanelRow>
                            </Accordion>
                        </div>
                    </Panel>
                ))) || (
                <Panel>
                    <PanelBodyText text={translate('survey.selectInObjectTreeDataPoint')} />
                </Panel>
            )}
        </Tray>
    );
}
